import { Link } from 'react-router-dom';
import {
  LegalPage,
  Section, P, Table, Th, Td, Note, Ol, B, Basis,
  useLegalDocumentHead,
} from './components';

/**
 * 위치기반서비스 이용약관.
 *
 * 개인정보처리방침(고지 문서)과 달리 이 문서는 **약관**이므로 이용자의 동의가 필요하다.
 * 앱 가입 화면의 "위치정보 수집 동의사항" 체크박스가 동의하는 대상이 이 약관이다.
 * (auth_agreements_bottom_sheet.dart)
 *
 * 구조와 위치정보법 조문 인용은 네이버 위치기반서비스 이용약관
 * (policy.naver.com/policy/service_location.html, 13개 조항 + 부칙)을 참조했다.
 *
 * 6조(확인자료 6개월)·10조(보유기간)는 개인정보처리방침 11.과 반드시 일치해야 한다.
 * 한쪽을 수정하면 다른 쪽도 함께 갱신할 것.
 */
export default function LocationServiceTerms() {
  useLegalDocumentHead('위치기반서비스 이용약관 | Cupid');

  return (
    <LegalPage title="위치기반서비스 이용약관" subtitle="시행일자: 2026년 10월 1일">

        {/*
          ⚠️ 게시 전 남은 작업
          2. useLegalDocumentHead 두 번째 인자를 false로 바꿔 색인 허용
          3. 법률 검토 — 위치기반서비스사업 신고는 해당 확정(2026-08-17), 약관 제출 전 검토 권장
          4. 제6조 확인자료 기록 ✅ 구현 완료(2026-08-17, location_usage_records).
             단 DDL(docs/sql/location_usage_records.sql)은 운영 DB 실행 필요.
             → cupid-application-server/docs/2026_08_17_privacy-policy-draft.md 5장
        */}

        <Section no={1} title="목적">
          <P>
            이 약관은 Cupid(이하 "회사")가 제공하는 위치기반서비스와 관련하여 회사와 개인위치정보주체와의 권리, 의무 및
            책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.
          </P>
        </Section>

        <Section no={2} title="약관 외 준칙">
          <P>
            이 약관에 명시되지 않은 사항은 「위치정보의 보호 및 이용 등에 관한 법률」, 「개인정보 보호법」, 「정보통신망 이용촉진
            및 정보보호 등에 관한 법률」, 「전기통신기본법」, 「전기통신사업법」 등 관계 법령과 회사의 서비스 이용약관 및{' '}
            <Link to="/privacy" className="text-red-400 hover:text-red-300">개인정보처리방침</Link>, 회사가 별도로 정한 지침
            등에 의합니다.
          </P>
        </Section>

        <Section no={3} title="서비스 내용 및 요금">
          <P>① 회사는 개인위치정보주체의 위치정보를 이용하여 아래와 같은 위치기반서비스를 제공합니다.</P>
          <Ol>
            <li>
              <B>위치 기반 그룹 매칭</B>: 개인위치정보주체가 매칭 기능을 시작한 시점의 위치를 기준으로 반경 5km 이내에서 매칭
              조건(성별, 연령대, 인원수)에 부합하는 상대 그룹을 검색하여 제안합니다.
            </li>
            <li>
              <B>매칭 상대와의 거리 정보 제공</B>: 제안된 상대 그룹과의 대략적인 거리를 표시합니다. 정확한 위치나 좌표는
              제공되지 않습니다.
            </li>
          </Ol>
          <P>
            ② 회사는 위치정보를 <B>매칭 기능이 실행되는 동안에만</B> 수집·이용하며, 앱을 사용하지 않는 동안에는 위치를
            수집하지 않습니다.
          </P>
          <P>
            ③ 제1항의 위치기반서비스 이용 자체에 대한 별도 요금은 없습니다. 다만 그룹 매칭이 성사되는 경우 서비스 내 재화인
            "하트"가 참여 구성원별로 1개 차감됩니다. 그룹 생성·참여 단계에서는 하트 보유 여부만 확인하며 차감되지 않습니다.
          </P>
        </Section>

        <Section no={4} title="개인위치정보주체의 권리">
          <P>
            ① 개인위치정보주체는 개인위치정보 수집 범위 및 이용약관의 내용 중 일부 또는 개인위치정보의 이용·제공 목적,
            제공받는 자의 범위 및 위치기반서비스의 일부에 대하여 동의를 유보할 수 있습니다.
          </P>
          <P>② 개인위치정보주체는 개인위치정보의 수집·이용·제공에 대한 동의의 전부 또는 일부를 철회할 수 있습니다.</P>
          <P>
            ③ 개인위치정보주체는 언제든지 개인위치정보의 수집·이용·제공의 일시적인 중지를 요구할 수 있습니다. 이 경우 회사는
            요구를 거절하지 아니하며, 이를 위한 기술적 수단을 갖추고 있습니다. 개인위치정보주체는 기기의 설정에서 위치 권한을
            해제하거나 매칭 기능을 시작하지 않음으로써 수집을 중지할 수 있습니다.
          </P>
          <P>
            ④ 개인위치정보주체는 회사에 대하여 아래 자료의 열람 또는 고지를 요구할 수 있고, 당해 자료에 오류가 있는 경우에는 그
            정정을 요구할 수 있습니다. 이 경우 회사는 정당한 이유 없이 요구를 거절하지 아니합니다.
          </P>
          <Ol>
            <li>개인위치정보주체에 대한 위치정보 수집·이용·제공사실 확인자료</li>
            <li>
              개인위치정보주체의 개인위치정보가 「위치정보의 보호 및 이용 등에 관한 법률」 또는 다른 법령의 규정에 의하여
              제3자에게 제공된 이유 및 내용
            </li>
          </Ol>
          <P>
            ⑤ 회사는 개인위치정보주체가 동의의 전부 또는 일부를 철회한 경우에는 지체 없이 수집된 개인위치정보 및 위치정보
            수집·이용·제공사실 확인자료를 파기합니다. 단, 동의의 일부를 철회하는 경우에는 철회하는 부분의 개인위치정보 및
            위치정보 수집·이용·제공사실 확인자료에 한합니다.
          </P>
          <P>⑥ 개인위치정보주체는 제1항 내지 제4항의 권리행사를 위하여 이 약관 제13조의 연락처를 이용하여 회사에 요구할 수 있습니다.</P>
          <Note>
            위치정보 제공에 동의하지 않거나 동의를 철회하는 경우 <B>그룹 매칭 기능만 이용할 수 없으며</B>, 친구 관리·채팅 등
            그 외 서비스는 정상적으로 이용할 수 있습니다.
          </Note>
        </Section>

        <Section no={5} title="법정대리인의 권리">
          <P>
            본 서비스는 <B>만 19세 이상만 이용할 수 있으므로</B> 회사는 만 14세 미만 아동의 개인위치정보를 수집·이용 또는
            제공하지 않습니다. 따라서 이 조에 따른 법정대리인의 권리 행사가 발생하는 경우는 없습니다.
          </P>
        </Section>

        <Section no={6} title="위치정보 수집·이용·제공사실 확인자료 보유근거 및 보유기간">
          <P>
            회사는 「위치정보의 보호 및 이용 등에 관한 법률」 제16조 제2항에 근거하여 개인위치정보주체에 대한 위치정보
            수집·이용·제공사실 확인자료를 위치정보시스템에 자동으로 기록하며, <B>6개월간</B> 보관합니다.
          </P>
          <Basis>「위치정보의 보호 및 이용 등에 관한 법률」 제16조 제2항</Basis>
        </Section>

        <Section no={7} title="서비스의 변경 및 중지">
          <P>
            ① 회사는 회사의 제반 사정 또는 법률상의 장애 등으로 서비스를 유지할 수 없는 경우, 위치기반서비스의 전부 또는 일부를
            제한·변경하거나 중지할 수 있습니다.
          </P>
          <P>② 제1항에 의한 서비스 중단의 경우에는 회사는 사전에 앱 내 공지사항 등을 통해 공지하거나 개인위치정보주체에게 통지합니다.</P>
        </Section>

        <Section no={8} title="개인위치정보 제3자 제공 시 즉시 통보">
          <P>
            ① 회사는 <B>개인위치정보주체의 동의 없이 개인위치정보를 제3자에게 제공하지 아니하며</B>, 제3자 제공 서비스를
            제공하는 경우에는 제공받는 자 및 제공목적을 사전에 개인위치정보주체에게 고지하고 동의를 받습니다.
          </P>
          <P>
            ② 회사는 개인위치정보를 개인위치정보주체가 지정하는 제3자에게 제공하는 경우에는 개인위치정보를 수집한 당해
            통신단말장치로 매회 개인위치정보주체에게 제공받는 자, 제공일시 및 제공목적을 즉시 통보합니다.
          </P>
          <P>③ 다만, 아래에 해당하는 경우에는 개인위치정보주체가 미리 특정하여 지정한 통신단말장치 또는 전자우편주소 등으로 통보합니다.</P>
          <Ol>
            <li>개인위치정보를 수집한 당해 통신단말장치가 문자, 음성 또는 영상의 수신기능을 갖추지 아니한 경우</li>
            <li>
              개인위치정보주체가 개인위치정보를 수집한 당해 통신단말장치 외의 통신단말장치 또는 전자우편주소 등으로 통보할 것을
              미리 요청한 경우
            </li>
          </Ol>
          <Note>
            <B>현재 회사는 개인위치정보를 제3자에게 제공하지 않습니다.</B> 매칭 상대 그룹에게는 정확한 위치나 좌표가 아닌
            대략적인 거리만 표시되며, 이는 서비스의 본질적 기능으로서 제3자 제공에 해당하지 않습니다.
          </Note>
        </Section>

        <Section no={9} title="8세 이하의 아동 등의 보호의무자의 권리">
          <P>
            본 서비스는 만 19세 이상만 이용할 수 있으므로 8세 이하의 아동 등이 이용자가 되는 경우는 없습니다. 다만
            「위치정보의 보호 및 이용 등에 관한 법률」에 따른 안내를 위해 관련 내용을 기재합니다.
          </P>
          <P>
            ① 회사는 아래의 경우에 해당하는 자(이하 "8세 이하의 아동 등"이라 함)의 보호의무자가 8세 이하의 아동 등의 생명 또는
            신체보호를 위하여 개인위치정보의 이용 또는 제공에 동의하는 경우에는 본인의 동의가 있는 것으로 봅니다.
          </P>
          <Ol>
            <li>8세 이하의 아동</li>
            <li>피성년후견인</li>
            <li>
              「장애인복지법」 제2조 제2항 제2호의 규정에 의한 정신적 장애를 가진 자로서 「장애인고용촉진 및 직업재활법」
              제2조 제2호의 규정에 의한 중증장애인에 해당하는 자(「장애인복지법」 제32조의 규정에 의하여 장애인등록을 한 자에
              한정)
            </li>
          </Ol>
          <P>
            ② 8세 이하의 아동 등의 생명 또는 신체의 보호를 위하여 개인위치정보의 이용 또는 제공에 동의를 하고자 하는
            보호의무자는 서면동의서에 보호의무자임을 증명하는 서면을 첨부하여 회사에 제출하여야 합니다.
          </P>
          <P>
            ③ 보호의무자는 8세 이하의 아동 등의 개인위치정보 이용 또는 제공에 동의하는 경우 개인위치정보주체 권리의 전부를
            행사할 수 있습니다.
          </P>
        </Section>

        <Section no={10} title="개인위치정보의 보유목적 및 이용기간">
          <P>① 회사는 위치기반서비스를 제공하기 위해 필요한 최소한의 기간 동안 개인위치정보를 보유 및 이용합니다.</P>
          <P>
            ② 회사는 개인위치정보를 그룹 매칭 목적으로 <B>일회성 또는 임시로 이용한 후 지체 없이 파기</B>합니다. 매칭이
            종료·중단되면 수집된 위치정보는 즉시 삭제되며, 별도로 축적하지 않습니다.
          </P>
          <P>
            ③ 회사는 개인위치정보를 재생이 불가능한 방법으로 안전하게 파기합니다. 전자적 파일 형태로 기록·저장된 개인위치정보는
            기록을 재생할 수 없도록 기술적인 방법을 이용하여 파기합니다.
          </P>
        </Section>

        <Section no={11} title="손해배상">
          <P>
            개인위치정보주체는 회사의 「위치정보의 보호 및 이용 등에 관한 법률」 제15조 내지 제26조의 규정을 위반한 행위로
            손해를 입은 경우에 회사에 대하여 손해배상을 청구할 수 있습니다. 이 경우 회사는 고의 또는 과실이 없음을 입증하지
            아니하면 책임을 면할 수 없습니다.
          </P>
          <Basis>「위치정보의 보호 및 이용 등에 관한 법률」 제15조 내지 제26조</Basis>
        </Section>

        <Section no={12} title="분쟁의 조정">
          <P>
            ① 회사는 위치정보와 관련된 분쟁에 대하여 개인위치정보주체와 협의가 이루어지지 아니하거나 협의를 할 수 없는 경우에는
            방송통신위원회에 재정을 신청할 수 있습니다.
          </P>
          <P>
            ② 회사 또는 개인위치정보주체는 위치정보와 관련된 분쟁에 대해 당사자간 협의가 이루어지지 아니하거나 협의를 할 수 없는
            경우에는 「개인정보 보호법」에 따라 개인정보분쟁조정위원회에 조정을 신청할 수 있습니다.
          </P>
        </Section>

        <Section no={13} title="사업자 정보 및 위치정보 관리책임자">
          <P>① 회사의 상호, 주소 및 연락처는 다음과 같습니다.</P>
          <Table>
            <thead><tr><Th colSpan={2}>사업자 정보</Th></tr></thead>
            <tbody>
              <tr><Td>상호</Td><Td>Cupid</Td></tr>
              <tr><Td>사업자등록번호</Td><Td>438-37-01587</Td></tr>
              <tr><Td>대표자</Td><Td>정지성</Td></tr>
              <tr><Td>주소</Td><Td>서울특별시 송파구 충민로 10, 8층 S16-M117호(문정동, 가든파이브툴)</Td></tr>
              <tr><Td>전화번호</Td><Td><a className="text-red-400 hover:text-red-300" href="tel:010-4027-8182">010-4027-8182</a></Td></tr>
            </tbody>
          </Table>
          <P>
            ② 회사는 다음과 같이 위치정보 관리책임자를 지정하여 이용자들이 서비스 이용과정에서 발생한 민원사항 처리를 비롯하여
            개인위치정보주체의 권리 보호를 위해 힘쓰고 있습니다.
          </P>
          <Table>
            <thead><tr><Th colSpan={2}>위치정보 관리책임자</Th></tr></thead>
            <tbody>
              <tr><Td>성명</Td><Td>정지성 (개인정보 보호책임자 겸직)</Td></tr>
              <tr><Td>직책</Td><Td>대표</Td></tr>
              <tr><Td>연락처</Td><Td><a className="text-red-400 hover:text-red-300" href="tel:010-4027-8182">010-4027-8182</a></Td></tr>
              <tr>
                <Td>전자우편</Td>
                <Td><a className="text-red-400 hover:text-red-300" href="mailto:support@cupidlab.io">support@cupidlab.io</a></Td>
              </tr>
            </tbody>
          </Table>
        </Section>

        <Section title="부칙">
          <P>이 약관은 2026년 10월 1일부터 시행합니다.</P>
        </Section>

    </LegalPage>
  );
}
